import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { enrichProfile, getContactDetails } from "../../../apis/enrich";
import BulkPhoneEmailButton from "./BulkPhoneEmailButton";
import { Skeleton } from "../../ui/skeleton";
import StripeLoader from "../../StripeLoader";
import EnrichedPhone from "../../ext/EnrichedPhone";
import EnrichedEmail from "../../ext/EnrichedEmail";
import SharedDataContext from "../../../SharedDataContext";
import useFeatureFlag from "../../../hooks/useFeatureFlag";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { Menu, Transition } from "@headlessui/react";
import useModal from "../../GenericModal/useModal";
import GenericModal from "../../GenericModal";

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

export default function RevealContact({
  contactId,
  activeWorkspace,
  isBulkRevealing,
  openProfileIframe,
  getRevealedInfo,
  fetchingRevealDetails,
}) {
  const userData = useContext(SharedDataContext);
  const isDncFilteringEnabled = useFeatureFlag("dnc-number-filtering");
  const [contact, setContact] = useState({});
  const getContactDetailsFn = useMutation("get_contact_details", getContactDetails, {
    onSuccess(data, variables, context) {
      console.log(data);
      if (data && data.contact) {
        setContact(data.contact);
      }
      // if (
      //   data &&
      //   data.contact &&
      //   data.contact.phone_numbers &&
      //   data.contact.phone_numbers.length === 0 &&
      //   data.contact.email_addresses &&
      //   data.contact.email_addresses.length === 0
      // ) {
      //   enrichProfileDetails();
      // }
      if (data.contact.enrichment_timeline && data.contact.enrichment_timeline.length === 0) {
        getRevealedInfo(false);
      } else {
        getRevealedInfo(true);
      }
      fetchingRevealDetails(false);
    },
    onError(err) {
      fetchingRevealDetails(false);
    },
  });
  const handleGetContactDetails = () => {
    fetchingRevealDetails(true);
    getContactDetailsFn.mutate({
      contactId: contactId,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  useEffect(() => {
    if (activeWorkspace?.id) {
      handleGetContactDetails();
    }
  }, [activeWorkspace, contactId]);

  const enrichProfileFn = useMutation("enrich_profile", enrichProfile, {
    onSuccess(data, variables, context) {
      console.log("enrich_profile enriched", data);
      if (data && data.list && data.list.contacts && data.list.contacts.length > 0) {
        let contact = data.list.contacts[0];
        setContact(contact);
        // if (contactIsRevealed) {
        //   contactIsRevealed(contact);
        //   setShowContactDetails(true);
        // }
      }
    },
    onError(error) {
      console.log(error);
    },
  });

  const handleEnrichProfile = (payload) => {
    enrichProfileFn.mutate({
      data: {
        records: payload,
      },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  useEffect(() => {
    // This function is what you want to run at every interval

    const doPolling = () => {
      if (contact && contact.id && !enrichProfileFn.isLoading) {
        handleGetContactDetails();
      }
    };

    // Start polling
    const intervalId = setInterval(doPolling, 5000); // 5000ms = 5 seconds

    if (contact && contact.enrichment_timeline && contact.enrichment_timeline.length > 0) {
      const hasPendingInTimeline = contact.enrichment_timeline.some(
        (item) => item.status.toLowerCase() === "pending" || item.status.toLowerCase() === "processing"
      );
      const hasPendingInMobile =
        contact.phone_numbers &&
        contact.phone_numbers.length > 0 &&
        contact.phone_numbers.some(
          (item) => item.verification && item.verification.status.toLowerCase() === "pending"
        );
      const hasPendingInEmail =
        contact.email_addresses &&
        contact.email_addresses.length > 0 &&
        contact.email_addresses.some(
          (item) => item.verification && item.verification.status.toLowerCase() === "pending"
        );
      const hasPendingInMobileAdvanceVerification =
        contact.phone_numbers &&
        contact.phone_numbers.length > 0 &&
        contact.phone_numbers.some(
          (item) =>
            item.verification &&
            item.verification.advanced_verification &&
            (item.verification.advanced_verification.status.toLowerCase() === "pending" ||
              item.verification.advanced_verification.status.toLowerCase() === "processing")
        );
      if (!hasPendingInTimeline) {
        // If no items are 'Pending', stop polling
        // if (
        //   !hasPendingInMobile &&
        //   !hasPendingInEmail &&
        //   (refetchContact || enrichProfileFn.isSuccess)
        // ) {
        //   generateContactRevealedEvent(profile);
        // }
        if (!hasPendingInMobile && !hasPendingInEmail && !hasPendingInMobileAdvanceVerification) {
          clearInterval(intervalId);
        }
      }
    }
    if (contact && contact.enrichment_timeline && contact.enrichment_timeline.length === 0) {
      clearInterval(intervalId);
    }

    // Cleanup function to clear the interval when the component unmounts or the effect re-runs
    return () => clearInterval(intervalId);
  }, [contact, activeWorkspace && activeWorkspace.id]); // Empty dependency array means this effect runs only once on mount

  const showStripeLoader = () => {
    if (
      contact &&
      contact.enrichment_timeline &&
      contact.enrichment_timeline.some(
        (item) => item.status.toLowerCase() === "pending" || item.status.toLowerCase() === "processing"
      )
    ) {
      return (
        <div className="block w-full mx-auto absolute top-0">
          <StripeLoader />
        </div>
      );
    } else if (getContactDetailsFn.isLoading || enrichProfileFn.isLoading) {
      return (
        <div className="block w-full mx-auto absolute top-0">
          <StripeLoader />
        </div>
      );
    }
  };

  const processSourceData = (sourceData) => {
    // Group data by 'integration_config.integration.key'
    const grouped = sourceData.reduce((acc, item) => {
      const key = item?.integration_config?.integration?.key;
      if (!acc[key]) {
        acc[key] = {
          ...item,
          data_found: item.status === "Success",
          enrichment_data: [
            {
              enrichment_mode: item.enrichment_mode,
              status: item.status,
            },
          ],
        };
      } else {
        acc[key].enrichment_data.push({
          enrichment_mode: item.enrichment_mode,
          status: item.status,
        });
        // Update data_found if any of the statuses is 'Found'
        if (item.status === "Success") {
          acc[key].data_found = true;
        }
      }
      return acc;
    }, {});

    // Convert the grouped data back into an array and adjust fields
    return Object.values(grouped).map((item) => {
      // Remove fields that are not required in the final output
      delete item.enrichment_mode;
      delete item.status;
      return item;
    });
  };

  const filterByFound = (arr) => {
    return arr.filter((obj) => obj.data_found === true);
  };

  const shouldShowBlur = () => {
    if (contact && contact?.enrichment_timeline && contact?.enrichment_timeline.length === 0) {
      return true;
    } else if (
      contact &&
      contact.enrichment_timeline &&
      contact.enrichment_timeline.some(
        (item) => item.status.toLowerCase() === "pending" || item.status.toLowerCase() === "processing"
      ) &&
      filterByFound(processSourceData(contact.enrichment_timeline)).length === 0
    ) {
      return true;
    } else {
      return false;
    }
  };
  const isEnrichmentTimelineRunning = () => {
    if (
      contact &&
      contact.enrichment_timeline &&
      contact.enrichment_timeline.some(
        (item) => item.status.toLowerCase() === "pending" || item.status.toLowerCase() === "processing"
      )
    ) {
      return true;
    } else {
      return false;
    }
  };
  const { isOpen, openModal, closeModal } = useModal();
  const [showContactDetails, setShowContactDetails] = useState(false);
  const buttonRef = useRef();
  if (getContactDetailsFn.isLoading) {
    return <Skeleton className="h-9 w-[105px]" />;
  }

  return (
    <div>
      {contact.enrichment_timeline && contact.enrichment_timeline.length === 0 && (
        <button
          className="text-[14px] inline-flex justify-center items-center leading-none py-2 h-9 space-x-2 px-4 rounded-[8px] text-[#7F56D9] bg-white border border-[#7F56D9] font-semibold"
          onClick={() => {
            handleEnrichProfile([
              {
                contact_id: contact?.id ? contact?.id : "",
                enrichment_mode: "Email and Phone",
              },
            ]);
          }}
          disabled={enrichProfileFn.isLoading || isBulkRevealing ? true : false}
        >
          <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6.98356 7.71098C7.56356 8.919 8.35422 10.0512 9.35553 11.0525C10.3568 12.0538 11.4891 12.8445 12.6971 13.4245C12.801 13.4744 12.8529 13.4993 12.9187 13.5185C13.1523 13.5866 13.4392 13.5377 13.637 13.396C13.6927 13.3561 13.7403 13.3085 13.8356 13.2132C14.1269 12.9219 14.2726 12.7762 14.4191 12.681C14.9715 12.3218 15.6837 12.3218 16.2361 12.681C16.3825 12.7762 16.5282 12.9219 16.8196 13.2132L16.9819 13.3756C17.4248 13.8185 17.6462 14.0399 17.7665 14.2777C18.0058 14.7507 18.0058 15.3093 17.7665 15.7822C17.6462 16.0201 17.4248 16.2415 16.9819 16.6844L16.8506 16.8157C16.4092 17.2571 16.1886 17.4777 15.8885 17.6463C15.5556 17.8333 15.0385 17.9678 14.6567 17.9666C14.3126 17.9656 14.0774 17.8989 13.607 17.7654C11.0792 17.0479 8.69387 15.6942 6.70388 13.7042C4.7139 11.7142 3.36017 9.32888 2.6427 6.80105C2.50919 6.33068 2.44244 6.0955 2.44141 5.75137C2.44028 5.36952 2.57475 4.85245 2.76176 4.51952C2.9303 4.2195 3.15098 3.99882 3.59233 3.55747L3.72369 3.42611C4.16656 2.98324 4.388 2.7618 4.62581 2.64152C5.09878 2.40229 5.65734 2.40229 6.1303 2.64152C6.36812 2.7618 6.58955 2.98324 7.03242 3.42611L7.19481 3.5885C7.48615 3.87984 7.63182 4.0255 7.72706 4.17199C8.08622 4.7244 8.08622 5.43655 7.72706 5.98896C7.63182 6.13544 7.48615 6.28111 7.19481 6.57244C7.09955 6.6677 7.05192 6.71533 7.01206 6.77101C6.87038 6.96887 6.82146 7.25575 6.88957 7.48938C6.90873 7.55512 6.93367 7.60708 6.98356 7.71098Z"
              stroke="#6941C6"
              stroke-width="1.66667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>Reveal</span>
          {(enrichProfileFn.isLoading || isBulkRevealing) && (
            <div className="loader_circle inline-block"></div>
          )}
        </button>
      )}
      {contact.enrichment_timeline && contact.enrichment_timeline.length > 0 && (
        <span
          className="inline-block overflow-hidden rounded-lg relative"
          onClick={() => {
            if (isOpen) {
              closeModal();
            } else {
              openModal();
            }
          }}
        >
          {showStripeLoader()}
          <BulkPhoneEmailButton
            phonesLength={contact?.phone_numbers?.length}
            emailsLength={contact?.email_addresses?.length}
          />
        </span>
      )}
      {/* <Menu as="div" className="relative block text-left w-full">
        <Menu.Button className="w-full" ref={buttonRef}>
          fsfs
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            anchor="bottom start"
            className="bg-white border w-button-width rounded-lg mt-1 py-2"
          >
            <Menu.Item>
              <div>test</div>
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu> */}
      <GenericModal
        isOpen={isOpen}
        closeModal={closeModal}
        width="w-[400px]"
        title={`Contact Details of ${contact?.full_name}`}
      >
        {contact && contact?.enrichment_timeline && contact?.enrichment_timeline.length > 0 && (
          <div className="mt-6">
            <div className="h-[1px] bg-[#EAECF0] mx-auto my-3"></div>
            <div className="pl-1">
              {(() => {
                const filteredPhoneNumbers = isDncFilteringEnabled
                  ? contact?.phone_numbers?.filter((number) => !number.dnc)
                  : contact?.phone_numbers;
                return filteredPhoneNumbers && filteredPhoneNumbers.length > 0 ? (
                filteredPhoneNumbers.map((number, index) => (
                  <>
                    <EnrichedPhone
                      number={number}
                      type={number?.type}
                      phoneValue={number?.value}
                      isValid={number?.verification?.is_valid}
                      lineType={number?.verification?.line_type}
                      foundFrom={{
                        name: number?.integration_config?.integration?.display_name,
                        key: number?.integration_config?.integration?.key,
                      }}
                      feedback={number?.feedback}
                      value={
                        number?.verification?.status === "Completed"
                          ? number?.verification?.international_format
                            ? number?.verification?.international_format
                            : number?.value
                          : number?.value
                      }
                      activeWorkspace={activeWorkspace}
                      contactId={contact.id}
                      onSuccess={() => {
                        getContactDetailsFn.mutate({
                          id: contact.id,
                          options: {
                            headers: {
                              "X-Workspace-Id": activeWorkspace?.id,
                            },
                          },
                        });
                      }}
                      showDevider={index + 1 !== filteredPhoneNumbers.length ? true : false}
                      advanceVerification={{
                        verified_at_simple: number?.verification?.verified_at
                          ? dayjs(number?.verification?.verified_at).format("hh:mm A, MM/DD/YY")
                          : "",
                        status: number?.verification?.advanced_verification?.status,
                        verification_status: number?.verification?.advanced_verification?.verification_status,
                        verified_at: number?.verification?.advanced_verification?.verified_at
                          ? dayjs(number?.verification?.advanced_verification?.verified_at).format(
                              "hh:mm A, MM/DD/YY"
                            )
                          : "",
                      }}
                    />
                  </>
                ))
              ) : (
                <>
                  <>
                    {shouldShowBlur() ? (
                      <span className="text-[12px] block mt-2 blur-[2px] pointer-events-none unselectable">
                        +1 9876543210
                      </span>
                    ) : (
                      <>
                        {getContactDetailsFn.isLoading || isEnrichmentTimelineRunning() ? (
                          <Skeleton className="h-[20px] w-7/12 mt-2" />
                        ) : (
                          <span className="text-[12px] block mt-2">No phones found.</span>
                        )}
                      </>
                    )}
                  </>
                </>
              );
              })()}
            </div>
            <div className="pl-1">
              <div className="w-[240px] h-[1px] bg-[#EAECF0] mx-auto my-3"></div>

              {contact?.email_addresses && contact?.email_addresses.length > 0 ? (
                contact?.email_addresses.map((email, index) => (
                  <>
                    <EnrichedEmail
                      email={email}
                      contactId={contact.id}
                      feedback={email?.feedback}
                      foundFrom={{
                        name: email?.integration_config?.integration?.display_name,
                        key: email?.integration_config?.integration?.key,
                      }}
                      value={email?.value}
                      badge={email?.verification && email?.verification?.result}
                      activeWorkspace={activeWorkspace}
                      onSuccess={() => {
                        getContactDetailsFn.mutate({
                          id: contact.id,
                          options: {
                            headers: {
                              "X-Workspace-Id": activeWorkspace?.id,
                            },
                          },
                        });
                      }}
                      autoVerify={userData?.preferences?.chrome_ext_auto_verify_email ? true : false}
                      type={email?.type}
                      showDevider={index + 1 !== contact?.email_addresses.length ? true : false}
                      isVerifying={(status) => {
                        // setEmailVerifyStatus(status);
                      }}
                    />
                  </>
                ))
              ) : (
                <>
                  <>
                    {shouldShowBlur() ? (
                      <span className="text-[12px] block mt-2 blur-[2px] pointer-events-none unselectable">
                        reveal@example.com
                      </span>
                    ) : (
                      <>
                        {getContactDetailsFn.isLoading || isEnrichmentTimelineRunning() ? (
                          <Skeleton className="h-[20px] w-7/12 mt-2" />
                        ) : (
                          <span className="text-[12px] block mt-2">No emails found.</span>
                        )}
                      </>
                    )}
                  </>
                </>
              )}
            </div>
          </div>
        )}
      </GenericModal>
    </div>
  );
}
